/**
 * RouteModuleIntro — mounts the right `<ModuleIntro>` for the current route from
 * one place in the app shell (CLARID-UX-2026-06, PR 3).
 *
 * Rather than hand-editing ~50 pages, the shell resolves the active route to a
 * module key and renders its intro from the shared `helpContent`. Every mapped
 * page gets a consistent, dismissible intro with no per-page wiring. Pages that
 * render their own bespoke intro are listed in {@link SELF_INTRO} and skipped so
 * there's never a duplicate banner.
 */

import { resolveModuleKey } from '@/lib/help/moduleRoutes';
import type { ModuleKey } from '@/lib/help';
import ModuleIntroPanel from './ModuleIntroPanel';

/**
 * Modules that render their own intro inline — excluded from the central mount.
 * Keep in sync if a page adds or drops a hand-rolled `<ModuleIntro>`.
 */
const SELF_INTRO = new Set<ModuleKey>([
  'cra-lending-test', // CradHmdaLendingTestPage — bespoke, emphasis-styled
  'cra-lending-analysis', // CraLendingAnalysisPage — bespoke
  'crad-report', // CradPage — bespoke
  'enforcement-intelligence', // EnforcementIntelligencePage — bespoke
  'compliance-hub', // mounted inline in PR 2 (with ContributingModules)
  'exam-readiness', // mounted inline in PR 2 (with ModuleConnections)
]);

interface RouteModuleIntroProps {
  pathname: string;
}

export default function RouteModuleIntro({ pathname }: RouteModuleIntroProps) {
  const moduleKey = resolveModuleKey(pathname);
  if (!moduleKey || SELF_INTRO.has(moduleKey)) return null;

  return (
    <div className="mb-6">
      <ModuleIntroPanel moduleKey={moduleKey} />
    </div>
  );
}
